import {
    GoogleAuthProvider,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut,
} from "firebase/auth";

import { AuthAPI, SignInDTO } from "../infrastructure/api/auth.api";
import { auth } from "../infrastructure/firebase";

/**
 * Sign in against the backend. Tells whether the account still owes its
 * second factor. Errors (401, 403 `email_not_verified`...) propagate as they are.
 */
export async function signIn(data: SignInDTO) {
    const { data: result } = await AuthAPI.signIn(data);
    return result;
}

/** Sign in to Firebase with a password. Firebase errors propagate as they are. */
export async function signInWithPassword(email: string, password: string): Promise<void> {
    await signInWithEmailAndPassword(auth, email, password);
}

/** Sign in to Firebase and make the result this tab's current user. */
export async function openSession(email: string, password: string): Promise<void> {
    const result = await signInWithEmailAndPassword(auth, email, password);
    await auth.updateCurrentUser(result.user);
}

/**
 * Finish a sign-in that owes its second factor. Returns the therapist.
 *
 * The Firebase sign-in comes first: the server binds the verified code to
 * this very sign-in. A Google sign-in arrives already signed in, so it has no
 * password to pass.
 */
export async function verifyMfaSignIn(
    totpCode: string,
    email: string,
    password?: string,
) {
    if (password) {
        await openSession(email, password);
    }
    const { data } = await AuthAPI.mfaVerify({ totp_code: totpCode });
    // Pick up the claim the verification set: until the token is refreshed,
    // the API still treats this session as MFA-pending.
    await auth.currentUser?.getIdToken(true);
    return data;
}

/**
 * Give up on a sign-in that still owes its second factor. Drops whatever
 * Firebase session it opened, so no MFA-pending session outlives the dialog.
 */
export async function abandonMfaSignIn(): Promise<void> {
    await signOut(auth);
}

/** Sign in with a Google popup, then with the backend. */
export async function signInWithGoogle(inviteToken?: string) {
    await signInWithPopup(auth, new GoogleAuthProvider());
    const { data } = await AuthAPI.googleSignIn(inviteToken);
    return data;
}
